import { useState, useEffect, useRef } from 'react';
import { TH } from '../lib/constants.js';
import { isPhotoUrl, isThought, thoughtFontStyle, calcPrintPrice, pushToast, sizesForStyle, buildBookFormatSpec, clampBookPageCount } from '../lib/helpers.js';
import { generatePrintPdf, fetchPrintQuote, createPrintCheckout } from '../lib/api.js';
import { Btn, Back, Toggle } from '../components/Basic.jsx';
import { t as tr } from '../lib/i18n.js';
import { YR, DOY } from '../lib/dateState.js';
export function PrintPreviewView({t,book,go}){
  const bt=TH[book.theme]||t;
  const pagesWithCap=(book.pages||[]).filter(p=>p.caption);
  const highlights=(pagesWithCap.length>=3?pagesWithCap:(book.pages||[])).slice(-6).reverse();
  const total=(book.pages||[]).length;
  return(
    <div className="screen fade" style={{background:bt.bg}}>
      <div style={{padding:"14px 16px",display:"flex",alignItems:"center",gap:11,flexShrink:0,borderBottom:`1px solid ${bt.bord}`}}>
        <Back fn={()=>go(book.type==="micro"?"micro":"library")} t={bt}/>
        <span style={{fontSize:16,fontWeight:800,color:bt.tx,fontFamily:"Inter,sans-serif"}}>{tr("print_preview_header")}</span>
      </div>
      <div style={{flex:1,overflowY:"auto",padding:"18px 16px 20px"}}>
        {/* Cover mockup */}
        <div style={{background:bt.pill,borderRadius:20,padding:"26px 20px",textAlign:"center",marginBottom:20,position:"relative",overflow:"hidden",boxShadow:"0 10px 30px rgba(0,0,0,.18)"}}>
          <div style={{position:"absolute",inset:0,background:bt.pat,opacity:.4}}/>
          <div style={{position:"relative",zIndex:1}}>
            <div style={{fontSize:38,marginBottom:8}}>{bt.emoji}</div>
            <div style={{fontSize:19,fontWeight:800,color:"#fff",fontFamily:"Inter,sans-serif"}}>{book.title}</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,.75)",fontFamily:"Inter,sans-serif",marginTop:4}}>{tr("print_cover_stats",{total,pages:tr("feed_page_plural"),theme:bt.name})}</div>
          </div>
        </div>
        <div style={{fontSize:10,fontWeight:700,color:bt.soft,fontFamily:"Inter,sans-serif",letterSpacing:"1.5px",textTransform:"uppercase",marginBottom:10}}>{tr("print_highlights_label")}</div>
        {highlights.length>0?(
          <div style={{display:"flex",gap:10,overflowX:"auto",scrollbarWidth:"none",paddingBottom:6,marginBottom:20}}>
            {highlights.map((pg,i)=>(
              <div key={i} style={{flexShrink:0,width:118,background:bt.card,borderRadius:13,overflow:"hidden",border:`1px solid ${bt.bord}`}}>
                <div style={{height:100,background:`linear-gradient(135deg,${bt.ac}28,${bt.ac2||bt.ac}16)`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:38,position:"relative",overflow:"hidden"}}>
                  {isPhotoUrl(pg.photo)
                    ?<img src={pg.photo} style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover"}}/>
                    :isThought(pg.photo)
                      ?<div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",padding:6,overflow:"hidden"}}><span style={{...thoughtFontStyle(pg.photo),fontSize:9,lineHeight:1.25,color:bt.tx,textAlign:"center",display:"-webkit-box",WebkitLineClamp:5,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{pg.caption||"✏️"}</span></div>
                      :pg.photo}
                </div>
                <div style={{padding:"7px 9px 9px"}}>
                  <div style={{fontSize:8,color:bt.soft,fontFamily:"Inter,sans-serif",marginBottom:2}}>{tr("feed_page_abbr")} {pg.day}</div>
                  {pg.caption&&<div style={{fontSize:10,color:bt.tx,fontFamily:"Inter,sans-serif",fontStyle:"italic",lineHeight:1.3}}>{pg.caption}</div>}
                </div>
              </div>
            ))}
          </div>
        ):(
          <div style={{textAlign:"center",padding:"20px",color:bt.soft,fontFamily:"Inter,sans-serif",fontSize:12,marginBottom:20}}>{tr("print_highlights_empty")}</div>
        )}
        {/* Stats */}
        <div style={{display:"flex",gap:9,marginBottom:8}}>
          {[
            {icon:"📄",label:`${total} ${tr("feed_page_plural")}`},
            {icon:"🎨",label:bt.name},
            {icon:"📖",label:book.type==="micro"?tr("print_stat_microbook"):tr("print_stat_book",{year:book.year})},
          ].map((s,i)=>(
            <div key={i} style={{flex:1,background:bt.card,borderRadius:12,border:`1px solid ${bt.bord}`,padding:"12px 8px",textAlign:"center"}}>
              <div style={{fontSize:18,marginBottom:4}}>{s.icon}</div>
              <div style={{fontSize:10,color:bt.tx,fontFamily:"Inter,sans-serif",fontWeight:600}}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{padding:"12px 16px 26px",flexShrink:0}}>
        <Btn full fn={()=>go("print_format",{book})} style={{background:bt.pill,color:"#fff",padding:"15px",borderRadius:40,fontSize:14,fontWeight:700,fontFamily:"Inter,sans-serif",textAlign:"center",boxShadow:`0 7px 20px ${bt.ac}44`}}>{tr("print_continue_cta")}</Btn>
      </div>
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────
// Marchio Oncer (l'anello-fotocamera dentro il nastro/segnalibro) ricalcato
// dal path reale dell'icona app (icon-only.svg) — usato come "timbro" sui
// fac-simile qui sotto, invece del rettangolo vuoto generico di prima, così
// il libro sembra davvero un prodotto Oncer e non un placeholder qualsiasi.
function OncerMark({size=24,color="#fff",opacity=.92}){
  return(
    <svg width={size} height={size} viewBox="0 0 2048 2048" style={{opacity}}>
      <path d="M717.6 389.1 L1330.4 389.1 A104.3 104.3 0 0 1 1434.8 493.5 L1434.8 1658.9 L1024 1373.2 L613.2 1658.9 L613.2 493.5 A104.3 104.3 0 0 1 717.6 389.1 Z" fill={color} fillOpacity=".22"/>
      <path fillRule="evenodd" clipRule="evenodd" d="M1299.2 884.3 a275.2 275.2 0 1 0 -550.4 0 a275.2 275.2 0 1 0 550.4 0 Z M1186.7 884.3 a162.7 162.7 0 1 1 -325.3 0 a162.7 162.7 0 1 1 325.3 0 Z" fill={color}/>
    </svg>
  );
}
// ─────────────────────────────────────────────────────────────────────────
// Fac-simile 3D del libro — riscritto il 31/08/2026 dopo moltissimi round di
// prove dal vivo con Matte (mockup interattivo in chat, non questo file) per
// arrivare a un'animazione che sembri un libro vero e non un disegno finto:
// 1) si sceglie una copertina → il libro sfoglia DAVVERO pagine (non un
//    dettaglio che luccica), copertina e pagine si aprono insieme;
// 2) le pagine si richiudono da sole, ma la copertina resta aperta come
//    "fermo" — è quello il punto in cui si vede lo spessore vero del libro;
// 3) "Continua" fa scorrere avanti SOLO la prima pagina (quella subito sotto
//    la copertina) mostrando un fac-simile vero del layout 1/2/4 foto/pagina
//    con date, non un placeholder generico.
// Bug reale trovato e risolto in un tentativo precedente: le FAN_N pagine
// vanno numerate con l'indice 0 = quella più vicina alla copertina (quella
// che poi scorre per il layout). Numerandole al contrario, le altre pagine
// finiscono DAVANTI a quella 0 e la coprono quando dovrebbe scorrere.
const FAN_N=24;
const MOCK_READ_ANGLE=-16, MOCK_READ_ANGLE_FLAT=-5;
const MOCK_PAGE_BEND=-7; // leggerissima piega delle pagine, solo con copertina morbida
function mockOpenAngle(style){ return style==="layflat"?-84:-78; }
// Solo la metà delle pagine più vicine alla copertina arriva del tutto in
// appoggio quando sfoglia; l'altra metà (verso il dorso) si apre solo in
// parte — un libro vero non porta mai tutte le pagine allo stesso punto,
// altrimenti l'animazione sembra finta/meccanica (nota di Matte).
function mockPageFanTarget(i,openA){
  const half=FAN_N/2;
  if(i<half) return openA;
  const tt=(i-half)/(half-1);
  return openA*(0.6-tt*0.2);
}
// Fac-simile vero del layout scelto (1/2/4 foto/pagina) con date di esempio
// — non un placeholder generico, per far capire davvero la differenza tra
// le tre opzioni, come chiesto da Matte.
function MockPageContent({pp}){
  const dates=["3 mar","4 mar","5 mar","6 mar"];
  const cellBig=(lbl,i)=>(
    <div key={i} style={{position:"relative",flex:1,width:"100%",background:"linear-gradient(135deg,rgba(0,0,0,.05),rgba(0,0,0,.015))",borderRadius:4,overflow:"hidden",display:"flex",flexDirection:"column",height:"100%",boxSizing:"border-box"}}>
      <div style={{padding:"5px 7px 3px",fontSize:8,letterSpacing:".6px",textTransform:"uppercase",color:"#b3893f",fontWeight:700,fontFamily:"Inter,sans-serif"}}>{lbl}</div>
      <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,opacity:.4}}>📷</div>
      <div style={{padding:"0 7px 7px",display:"flex",flexDirection:"column",gap:3}}>
        <div style={{height:2,borderRadius:1,background:"rgba(0,0,0,.16)",width:"85%"}}/>
        <div style={{height:2,borderRadius:1,background:"rgba(0,0,0,.16)",width:"55%"}}/>
      </div>
    </div>
  );
  const cellSmall=(lbl,i)=>(
    <div key={i} style={{position:"relative",background:"linear-gradient(135deg,rgba(0,0,0,.06),rgba(0,0,0,.015))",borderRadius:3,overflow:"hidden",height:"100%",boxSizing:"border-box"}}>
      <div style={{position:"absolute",top:3,left:4,fontSize:5.5,letterSpacing:".4px",textTransform:"uppercase",color:"#b3893f",fontWeight:700,fontFamily:"Inter,sans-serif",zIndex:1}}>{lbl}</div>
      <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,opacity:.4}}>📷</div>
    </div>
  );
  const gridStyle=pp===1?{display:"flex"}:pp===2?{display:"grid",gridTemplateRows:"1fr 1fr",gap:3}:{display:"grid",gridTemplateColumns:"1fr 1fr",gridTemplateRows:"1fr 1fr",gap:3};
  return(
    <div style={{position:"absolute",inset:3,padding:4,boxSizing:"border-box",background:"linear-gradient(120deg,#fff,#f6f3ea)",borderRadius:2,...gridStyle}}>
      {Array.from({length:pp}).map((_,i)=>pp===1?cellBig(dates[i%dates.length],i):cellSmall(dates[i%dates.length],i))}
    </div>
  );
}
// Scena 3D del libro: retro + dorso + FAN_N pagine sottili impilate +
// copertina, tutte transform-origin sul bordo sinistro (il dorso) così
// aprendosi ruotano davvero come pagine vere, non un disegno statico.
// Niente più tagli pagina sopra/sotto/a-destra come in una versione
// precedente: con le pagine vere che si aprono per davvero, quei pannelli
// piatti aggiuntivi sembravano un "involucro finto" separato — segnalato da
// Matte e tolti del tutto.
function BookClosedBox({t,style,W,H,depth,pagesRef,coverRef,readingPp}){
  const isSoft=style==="softcover";
  const frontRadius=isSoft?"2px 8px 8px 2px":"2px 7px 7px 2px";
  const coverBgImage=isSoft?`linear-gradient(135deg,${t.ac},${t.ac2||t.ac})`:`repeating-linear-gradient(115deg,rgba(255,255,255,.07) 0 2px,transparent 2px 5px),linear-gradient(135deg,${t.ac},${t.ac2||t.ac})`;
  // Copertina morbida: NESSUN pezzo separato ruotato. Provato a lungo (vedi
  // cronologia con Matte) a costruire la curva con più fasce ruotate — il
  // rendering 3D dei browser lascia quasi sempre una riga visibile alla
  // giunzione tra un pezzo e l'altro, indipendentemente da quanto si
  // sovrappongono. La curva qui è resa SOLO con luce/ombra su un pannello
  // UNICO: zero pezzi, zero righe possibili.
  const softShade=isSoft&&<div style={{position:"absolute",inset:0,background:"linear-gradient(90deg,transparent 0%,transparent 22%,rgba(0,0,0,.42) 50%,transparent 78%,transparent 100%)",pointerEvents:"none"}}/>;
  const softGlow=isSoft&&<div style={{position:"absolute",inset:0,background:"linear-gradient(90deg,rgba(255,255,255,.3) 0%,transparent 26%,transparent 74%,rgba(255,255,255,.3) 100%)",pointerEvents:"none"}}/>;
  const groove=!isSoft&&<div style={{position:"absolute",left:9,top:5,bottom:5,width:1,background:"rgba(0,0,0,.28)",boxShadow:"1px 0 0 rgba(255,255,255,.15)"}}/>;
  return(
    <div style={{position:"relative",width:W,height:H,transformStyle:"preserve-3d"}}>
      {/* retro */}
      <div style={{position:"absolute",width:W,height:H,borderRadius:frontRadius,background:`linear-gradient(135deg,${t.ac2||t.ac},${t.ac})`,transform:`rotateY(180deg) translateZ(${depth/2}px)`}}/>
      {/* pagine a ventaglio — indice 0 = subito sotto la copertina (vedi nota sopra) */}
      {Array.from({length:FAN_N}).map((_,i)=>{
        const z=(depth/2-1)-(depth-2)*(i+1)/(FAN_N+1);
        const isFront=i===0;
        return(
          <div key={i} ref={el=>{ if(pagesRef&&pagesRef.current) pagesRef.current[i]=el; }} data-z={z}
            style={{position:"absolute",width:W-6,height:H-6,left:3,top:3,
              background:isSoft?undefined:"linear-gradient(160deg,#fbf8f0,#efe9db)",
              borderRadius:"2px 5px 5px 2px",transformOrigin:"left center",
              transform:`translateZ(${z}px) rotateY(0deg)`,boxShadow:"0 1px 3px rgba(0,0,0,.1)",overflow:"hidden"}}>
            {isFront&&readingPp?(
              <MockPageContent pp={readingPp}/>
            ):(
              <>
                {isSoft&&(<>
                  <div style={{position:"absolute",top:0,left:0,width:"54%",height:"100%",background:"linear-gradient(160deg,#fbf8f0,#efe9db)"}}/>
                  <div style={{position:"absolute",top:0,left:"49%",width:"54%",height:"100%",background:"linear-gradient(160deg,#fbf8f0,#efe9db)",transformOrigin:"left center",transform:`rotateY(${MOCK_PAGE_BEND}deg)`}}/>
                </>)}
                {!isFront&&(<>
                  <div style={{position:"absolute",left:"12%",right:"16%",top:"24%",height:1.5,background:"rgba(0,0,0,.05)"}}/>
                  <div style={{position:"absolute",left:"12%",right:"34%",top:"38%",height:1.5,background:"rgba(0,0,0,.05)"}}/>
                  <div style={{position:"absolute",left:"12%",right:"24%",top:"52%",height:1.5,background:"rgba(0,0,0,.05)"}}/>
                </>)}
              </>
            )}
          </div>
        );
      })}
      {/* copertina */}
      <div ref={coverRef} data-z={depth/2} style={{position:"absolute",width:W,height:H,borderRadius:frontRadius,
        boxShadow:isSoft?"0 6px 16px rgba(0,0,0,.22)":"0 16px 30px rgba(0,0,0,.4)",
        border:isSoft?"1px solid rgba(255,255,255,.2)":"2px solid rgba(255,255,255,.35)",
        overflow:"hidden",transformOrigin:"left center",transform:`translateZ(${depth/2}px) rotateY(0deg)`,
        backgroundImage:coverBgImage,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:4}}>
        {softShade}{softGlow}{groove}
        <OncerMark size={Math.round(W*.19)}/>
        <div style={{fontSize:9,fontWeight:700,letterSpacing:2,color:"rgba(255,255,255,.94)",fontFamily:"Inter,sans-serif"}}>ONCER</div>
      </div>
      {/* dorso */}
      <div style={{position:"absolute",width:depth,height:H-4,top:2,left:(W-depth)/2,background:`linear-gradient(135deg,${t.ac},${t.ac2||t.ac})`,transform:`rotateY(-90deg) translateZ(${W/2}px)`,boxShadow:"inset 5px 0 8px rgba(0,0,0,.4)",display:"flex",alignItems:"center",justifyContent:"center"}}>
        <span style={{writingMode:"vertical-rl",fontSize:6.5,letterSpacing:1,color:"rgba(255,255,255,.8)",fontWeight:700,fontFamily:"Inter,sans-serif"}}>ONCER</span>
      </div>
    </div>
  );
}
// Orchestratore: guida via ref l'apertura/chiusura reale del libro (non
// stato React + CSS declarativo, perché servono transizioni sfalsate pagina
// per pagina con durate diverse — più semplice e affidabile manipolare il
// transform direttamente sui nodi DOM, esattamente come nel prototipo
// validato dal vivo con Matte). Il CONTENUTO della prima pagina (il fac-
// simile del layout) resta invece stato React vero (readingPp), così si
// aggiorna in modo normale quando cambia la prop photosPerPage.
// Riscritto il 31/08/2026 dopo il primo test in app di Matte, due bug reali:
// 1) il libro si riapriva SOLO cambiando la copertina — cambiando taglia,
//    orientamento o numero di pagine l'animazione non ripartiva affatto.
//    Fix: l'effetto che pilota apri/chiudi ora dipende da TUTTE le props
//    che cambiano l'aspetto del libro, non solo da "style".
// 2) la scelta "quante foto per pagina" viveva DENTRO l'animazione (bottone
//    "Continua" che appariva solo a libro fermo, poi spariva quando si
//    tornava indietro) — una scelta che "scappava" facilmente all'utente.
//    Fix: quella scelta ORA è un toggle vero e sempre visibile, insieme
//    alle altre, dentro PrintFormatView (vedi photosPerPage passata come
//    prop qui sotto) — il mockup si limita a MOSTRARLA, senza più bottoni
//    "Continua"/"Torna alla copertina" nascosti al suo interno.
function BookMockup({t,style,wIn,hIn,pageCount,photosPerPage}){
  const maxDim=100;
  const ratio=(wIn||1)/(hIn||1);
  const h=ratio>=1?maxDim/ratio:maxDim;
  const w=ratio>=1?maxDim:maxDim*ratio;
  const isSoft=style==="softcover";
  const isFlat=style==="layflat";
  const boardExtra=style==="hardcover"?11:(isSoft?2:7);
  const depth=Math.max(9,Math.min(44,Math.round(((pageCount||60)/400)*44)))+boardExtra;
  const [phase,setPhase]=useState("closed"); // closed|opening|settled
  const [readingPp,setReadingPp]=useState(null);
  const pagesRef=useRef([]);
  const coverRef=useRef(null);
  const openedRef=useRef(false);
  const timers=useRef([]);
  function clearTimers(){ timers.current.forEach(clearTimeout); timers.current=[]; }
  function after(ms,fn){ const id=setTimeout(fn,ms); timers.current.push(id); }
  function setPageAngle(i,angle,dur,ease,delay){
    after(delay||0,()=>{
      const el=pagesRef.current[i];
      if(!el) return;
      const z=Number(el.dataset.z);
      el.style.transition=`transform ${dur}ms ${ease}`;
      requestAnimationFrame(()=>{ el.style.transform=`translateZ(${z}px) rotateY(${angle}deg)`; });
    });
  }
  function driveCover(curStyle,openA){
    const cover=coverRef.current;
    if(!cover) return;
    const cz=Number(cover.dataset.z);
    if(curStyle!=="softcover"){
      cover.style.transition="transform 300ms cubic-bezier(.4,0,.2,1)";
      cover.style.transform=`translateZ(${cz}px) rotateY(${openA}deg)`;
      return;
    }
    // Morbida: apertura decisa (subito quanto le pagine, mai in ritardo —
    // altrimenti le pagine la superano, bug reale trovato in un tentativo
    // precedente) seguita da un piccolo rimbalzo che non va MAI sotto openA.
    cover.style.transition="transform 400ms cubic-bezier(.22,.61,.36,1)";
    cover.style.transform=`translateZ(${cz}px) rotateY(${openA}deg)`;
    after(400,()=>{
      cover.style.transition="transform 260ms cubic-bezier(.34,1.56,.64,1)";
      cover.style.transform=`translateZ(${cz}px) rotateY(${openA-5}deg)`;
      after(260,()=>{
        cover.style.transition="transform 260ms cubic-bezier(.4,0,.2,1)";
        cover.style.transform=`translateZ(${cz}px) rotateY(${openA}deg)`;
      });
    });
  }
  function fanOpenThenSettle(curStyle,onDone){
    const isSoftNow=curStyle==="softcover";
    const openA=mockOpenAngle(curStyle);
    driveCover(curStyle,openA);
    const baseDur=isSoftNow?420:250, stagger=isSoftNow?14:9;
    const targets=[];
    for(let i=0;i<FAN_N;i++){
      const tgt=mockPageFanTarget(i,openA);
      targets[i]=tgt;
      setPageAngle(i,tgt,baseDur+i*stagger,"cubic-bezier(.4,0,.2,1)",i*stagger);
    }
    const phaseA=(FAN_N-1)*stagger+(baseDur+(FAN_N-1)*stagger)+(isSoftNow?200:40);
    after(phaseA+220,()=>{
      let maxDur=0;
      for(let i=0;i<FAN_N;i++){
        const dist=Math.abs(targets[i]);
        const dur=Math.round(190+dist*2.5);
        if(dur>maxDur) maxDur=dur;
        setPageAngle(i,0,dur,"cubic-bezier(.45,0,.55,1)",i*8);
      }
      after((FAN_N-1)*8+maxDur+70,()=>{ if(onDone) onDone(); });
    });
  }
  function fanClose(onDone){
    const cover=coverRef.current;
    if(cover){
      const cz=Number(cover.dataset.z);
      cover.style.transition="transform 280ms cubic-bezier(.4,0,.2,1)";
      cover.style.transform=`translateZ(${cz}px) rotateY(0deg)`;
    }
    for(let i=0;i<FAN_N;i++) setPageAngle(i,0,230+i*5,"cubic-bezier(.45,0,.55,1)",i*6);
    after((FAN_N-1)*6+230+(FAN_N-1)*5+70,()=>{ if(onDone) onDone(); });
  }
  function moveFrontPage(angle,dur,ease){
    const front=pagesRef.current[0];
    if(!front) return;
    const z=Number(front.dataset.z);
    front.style.transition=`transform ${dur}ms ${ease}`;
    requestAnimationFrame(()=>{ front.style.transform=`translateZ(${z}px) rotateY(${angle}deg)`; });
  }
  // Al montaggio e ad ogni cambio di QUALSIASI parte del libro (copertina,
  // taglia, orientamento, numero di pagine — non più solo "style", vedi nota
  // sopra): se il libro è già aperto lo richiude del tutto (copertina
  // compresa) e solo dopo riapre nella forma nuova — altrimenti si vedrebbe
  // un salto secco da una forma all'altra. A fine apertura, se è stata
  // passata una scelta foto/pagina (libro annuale), la prima pagina scorre
  // subito in avanti a mostrarla.
  useEffect(()=>{
    clearTimers();
    setReadingPp(null);
    const doOpen=()=>{
      setPhase("opening");
      fanOpenThenSettle(style,()=>{
        openedRef.current=true;
        setPhase("settled");
        if(photosPerPage){
          setReadingPp(photosPerPage);
          moveFrontPage(isFlat?MOCK_READ_ANGLE_FLAT:MOCK_READ_ANGLE,420,"cubic-bezier(.34,1.56,.64,1)");
        }
      });
    };
    if(openedRef.current) fanClose(doOpen);
    else doOpen();
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[style,wIn,hIn,pageCount]);
  // Cambio della SOLA scelta foto/pagina (il toggle vero, sempre visibile,
  // sta in PrintFormatView) mentre il libro è già fermo aperto: non serve
  // richiudere tutto, basta far scorrere la prima pagina verso il nuovo
  // contenuto — un piccolo tuffo e ritorno, come sfogliare davvero.
  useEffect(()=>{
    if(!openedRef.current||phase!=="settled") return;
    const angle=isFlat?MOCK_READ_ANGLE_FLAT:MOCK_READ_ANGLE;
    if(!photosPerPage){ setReadingPp(null); moveFrontPage(0,260,"cubic-bezier(.4,0,.2,1)"); return; }
    if(readingPp==null){ setReadingPp(photosPerPage); moveFrontPage(angle,420,"cubic-bezier(.34,1.56,.64,1)"); return; }
    if(readingPp===photosPerPage) return;
    moveFrontPage(10,180,"cubic-bezier(.4,0,.2,1)");
    after(190,()=>{ setReadingPp(photosPerPage); moveFrontPage(angle,300,"cubic-bezier(.34,1.56,.64,1)"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[photosPerPage]);
  const hintText=
    readingPp?(isFlat?tr("print_mockup_reading_layflat_hint"):tr("print_mockup_reading_hint")):
    isFlat?tr("print_mockup_layflat_open_hint"):isSoft?tr("print_mockup_soft_open_hint"):tr("print_mockup_hard_open_hint");
  return(
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",marginBottom:6}}>
      <div style={{perspective:1000,height:172,display:"flex",alignItems:"center",justifyContent:"center",width:"100%"}}>
        <div className="book3dIdle" style={{transformStyle:"preserve-3d"}}>
          <BookClosedBox t={t} style={style} W={w} H={h} depth={depth} pagesRef={pagesRef} coverRef={coverRef} readingPp={readingPp}/>
        </div>
      </div>
      <div style={{fontSize:10,color:t.soft,fontFamily:"Inter,sans-serif",marginTop:8,textAlign:"center",lineHeight:1.4,maxWidth:230,minHeight:34}}>{hintText}</div>
    </div>
  );
}
// Icona in miniatura usata DENTRO le singole card di scelta (softcover vs
// hardcover, piatto sì/no) — prima solo il grande mockup in cima cambiava
// forma, le card sotto erano testo puro e non si capiva la differenza senza
// prima cliccare. Segnalato da Matte il 30/08/2026 ("non si capisce bene").
// act (selezionata) fa fare alla mini-icona un piccolo "salto" con rimbalzo
// (spring, non un semplice fade) — lo stesso linguaggio di movimento del
// mockup grande qui sopra, così anche le card piccole della lista sentono
// "vive" e non solo un bordo colorato che cambia colore.
function MiniMockup({t,kind,act=false}){
  const box={width:44,height:36,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 8px",transform:act?"scale(1.1) translateY(-2px)":"scale(1)",transition:"transform .35s cubic-bezier(.34,1.56,.64,1)"};
  if(kind==="layflat_open"){
    return(
      <div style={{...box,gap:1.5}}>
        <div style={{width:19,height:30,borderRadius:"4px 0 0 4px",background:`linear-gradient(135deg,${t.ac}CC,${t.ac2||t.ac}AA)`,boxShadow:"0 3px 8px rgba(0,0,0,.18)"}}/>
        <div style={{width:19,height:30,borderRadius:"0 4px 4px 0",background:`linear-gradient(135deg,${t.ac2||t.ac}AA,${t.ac}CC)`,boxShadow:"0 3px 8px rgba(0,0,0,.18)"}}/>
      </div>
    );
  }
  if(kind==="layflat_closed"){
    return(
      <div style={box}>
        <div style={{width:26,height:32,borderRadius:3,background:`linear-gradient(135deg,${t.ac},${t.ac2||t.ac})`,boxShadow:"0 3px 8px rgba(0,0,0,.2)",border:"1px solid rgba(255,255,255,.25)"}}/>
      </div>
    );
  }
  const isSoft=kind==="softcover";
  return(
    <div style={box}>
      <div style={{position:"relative",width:26+((isSoft?3:5)),height:32}}>
        <div style={{position:"absolute",left:0,top:1,width:isSoft?3:5,height:30,background:t.ac,borderRadius:"2px 0 0 2px"}}/>
        <div style={{position:"absolute",left:isSoft?3:5,top:0,width:26,height:32,borderRadius:isSoft?"1px 7px 7px 1px":"1px 4px 4px 1px",background:`linear-gradient(135deg,${t.ac},${t.ac2||t.ac})`,boxShadow:isSoft?"0 3px 8px rgba(0,0,0,.15)":"0 4px 10px rgba(0,0,0,.22)",border:isSoft?"1px solid rgba(255,255,255,.2)":"1.5px solid rgba(255,255,255,.3)"}}/>
      </div>
    </div>
  );
}
// Wizard "scegli il tuo libro": ramificazione a toggle con fac-simile dal
// vivo, come richiesto — l'utente non deve mai sapere o scegliere tra
// Gelato/Prodigi, solo la forma del libro che vuole. Instradamento
// fornitore: le pagine layflat sono un'esclusiva Prodigi, quindi è la PRIMA
// domanda del wizard (se sì, il fornitore è già deciso); per copertina
// morbida/rigida entrambi i fornitori vanno bene e la scelta di quale usare
// avviene dal vivo lato server confrontando i preventivi reali (vedi
// get-print-quote), non qui.
export function PrintFormatView({t,book,go}){
  const bt=TH[book.theme]||t;
  const contentTotal=(book.pages||[]).length||1;
  const isAnnualBook=book.type==="annual";
  const [layflat,setLayflat]=useState(null); // null = non ancora risposto
  const [style,setStyle]=useState(null); // 'softcover'|'hardcover'|'layflat'
  const [sizeId,setSizeId]=useState(null);
  const [orientation,setOrientation]=useState(null);
  // Foto per pagina — SOLO libro annuale, stessa scelta reale usata dopo in
  // PrintOrderView (vedi isAnnualBook/photosPerPage lì). Qui è solo
  // un'anteprima: parte da 1 e viene passata come valore iniziale
  // all'ordine, dove resta comunque modificabile.
  const [pp,setPp]=useState(1);
  const sizes=style?sizesForStyle(style):[];
  const sizeDef=sizes.find(s=>s.id===sizeId);
  const needsOrientation=!!(sizeDef&&sizeDef.orientations.length>1);
  const canContinue=!!(style&&sizeId&&(!needsOrientation||orientation));
  // Spiega SEMPRE perché il bottone è spento invece di lasciarlo muto — bug
  // segnalato da Matte il 30/08/2026 ("esce il tasto continua spento e non
  // si capisce come mai, non viene spiegato nulla"). Un solo motivo alla
  // volta, nell'ordine in cui il wizard chiede le cose.
  const missingReason=canContinue?null
    :layflat===null?tr("print_wizard_missing_layflat")
    :!style?tr("print_wizard_missing_cover")
    :!sizeId?tr("print_wizard_missing_size")
    :tr("print_wizard_missing_orientation");
  const chooseLayflat=(yes)=>{
    setLayflat(yes);
    setStyle(yes?"layflat":null);
    setSizeId(null); setOrientation(null);
  };
  const chooseCover=(cov)=>{ setStyle(cov); setSizeId(null); setOrientation(null); };
  const chooseSize=(id)=>{
    setSizeId(id);
    const sd=sizes.find(s=>s.id===id);
    setOrientation(sd&&sd.orientations.length===1?sd.orientations[0]:null);
  };
  const continueNext=()=>{
    const spec=buildBookFormatSpec({style,sizeId,orientation,pageCount:contentTotal});
    go("print_order",{book,format:isAnnualBook?{...spec,photosPerPage:pp}:spec});
  };
  const pageWarn=style?clampBookPageCount(contentTotal,style):null;
  return(
    <div className="screen fade" style={{background:bt.bg,display:"flex",flexDirection:"column"}}>
      <div style={{padding:"14px 16px",display:"flex",alignItems:"center",gap:11,flexShrink:0,borderBottom:`1px solid ${bt.bord}`}}>
        <div onClick={()=>go("print_preview")} style={{width:32,height:32,borderRadius:"50%",background:bt.bord,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:14,color:bt.tx}}>←</div>
        <span style={{fontSize:16,fontWeight:800,color:bt.tx,fontFamily:"Inter,sans-serif"}}>{tr("print_wizard_header")}</span>
      </div>
      <div style={{flex:1,overflowY:"auto",padding:"18px 16px 20px"}}>
        <BookMockup t={bt} style={style||"hardcover"} wIn={sizeDef?sizeDef.wIn:8.3} hIn={sizeDef?sizeDef.hIn:8.3} pageCount={contentTotal} photosPerPage={isAnnualBook?pp:undefined}/>
        <div style={{textAlign:"center",fontSize:10,color:bt.soft,fontFamily:"Inter,sans-serif",marginBottom:22}}>{tr("print_wizard_vendor_note")}</div>
        {/* Step 1 — domanda che decide subito il fornitore */}
        <div style={{fontSize:13,fontWeight:700,color:bt.tx,fontFamily:"Inter,sans-serif",marginBottom:9}}>{tr("print_wizard_step_layflat_q")}</div>
        <div style={{display:"flex",gap:10,marginBottom:layflat!==null?6:20}}>
          {[{v:true,label:tr("print_wizard_step_layflat_yes"),mock:"layflat_open"},{v:false,label:tr("print_wizard_step_layflat_no"),mock:"layflat_closed"}].map(o=>{
            const act=layflat===o.v;
            return(
              <div key={String(o.v)} onClick={()=>chooseLayflat(o.v)} style={{flex:1,background:bt.card,borderRadius:14,padding:"13px 12px",textAlign:"center",border:act?"2px solid #FF2D6B":`1px solid ${bt.bord}`,cursor:"pointer",boxShadow:act?"0 3px 12px rgba(255,45,107,.15)":"none",transition:"border .2s,box-shadow .2s"}}>
                <MiniMockup t={bt} kind={o.mock} act={act}/>
                <div style={{fontSize:13,fontWeight:700,color:bt.tx,fontFamily:"Inter,sans-serif"}}>{o.label}</div>
              </div>
            );
          })}
        </div>
        {layflat===true&&<div style={{fontSize:11,color:bt.soft,fontFamily:"Inter,sans-serif",marginBottom:20,lineHeight:1.5}}>{tr("print_wizard_layflat_note")}</div>}
        {/* Step 2 — copertina, solo se non layflat */}
        {layflat===false&&(<>
          <div style={{fontSize:13,fontWeight:700,color:bt.tx,fontFamily:"Inter,sans-serif",marginBottom:9}}>{tr("print_wizard_step_cover_q")}</div>
          <div style={{display:"flex",gap:10,marginBottom:20}}>
            {[{id:"softcover",label:tr("print_wizard_cover_soft")},{id:"hardcover",label:tr("print_wizard_cover_hard")}].map(o=>{
              const act=style===o.id;
              return(
                <div key={o.id} onClick={()=>chooseCover(o.id)} style={{flex:1,background:bt.card,borderRadius:14,padding:"13px 12px",textAlign:"center",border:act?"2px solid #FF2D6B":`1px solid ${bt.bord}`,cursor:"pointer",boxShadow:act?"0 3px 12px rgba(255,45,107,.15)":"none",transition:"border .2s,box-shadow .2s"}}>
                  <MiniMockup t={bt} kind={o.id} act={act}/>
                  <div style={{fontSize:13,fontWeight:700,color:bt.tx,fontFamily:"Inter,sans-serif"}}>{o.label}</div>
                </div>
              );
            })}
          </div>
        </>)}
        {/* Step 3 — taglia, appena lo stile è deciso */}
        {style&&(<>
          <div style={{fontSize:13,fontWeight:700,color:bt.tx,fontFamily:"Inter,sans-serif",marginBottom:9}}>{tr("print_wizard_step_size_q")}</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:10,marginBottom:20}}>
            {sizes.map(s=>{
              const act=sizeId===s.id;
              const label=`${s.wIn.toFixed(1)}×${s.hIn.toFixed(1)}"`;
              return(
                <div key={s.id} onClick={()=>chooseSize(s.id)} style={{flex:"1 1 30%",minWidth:90,background:bt.card,borderRadius:14,padding:"12px 10px",textAlign:"center",border:act?"2px solid #FF2D6B":`1px solid ${bt.bord}`,cursor:"pointer",boxShadow:act?"0 3px 12px rgba(255,45,107,.15)":"none"}}>
                  <div style={{fontSize:12.5,fontWeight:700,color:bt.tx,fontFamily:"Inter,sans-serif"}}>{label}</div>
                </div>
              );
            })}
          </div>
        </>)}
        {/* Step 4 — orientamento, solo se la taglia ne ammette più di uno */}
        {needsOrientation&&(<>
          <div style={{fontSize:13,fontWeight:700,color:bt.tx,fontFamily:"Inter,sans-serif",marginBottom:9}}>{tr("print_wizard_step_orientation_q")}</div>
          <div style={{display:"flex",gap:10,marginBottom:20}}>
            {sizeDef.orientations.map(o=>{
              const act=orientation===o;
              const label=o==="portrait"?tr("print_wizard_orientation_portrait"):o==="landscape"?tr("print_wizard_orientation_landscape"):tr("print_wizard_orientation_square");
              return(
                <div key={o} onClick={()=>setOrientation(o)} style={{flex:1,background:bt.card,borderRadius:14,padding:"13px 12px",textAlign:"center",border:act?"2px solid #FF2D6B":`1px solid ${bt.bord}`,cursor:"pointer"}}>
                  <div style={{fontSize:13,fontWeight:700,color:bt.tx,fontFamily:"Inter,sans-serif"}}>{label}</div>
                </div>
              );
            })}
          </div>
        </>)}
        {/* Foto per pagina — SOLO libro annuale. Toggle vero e sempre
            visibile insieme alle altre scelte qui sopra (non più un bottone
            nascosto dentro l'animazione del libro, che segnalato da Matte
            "poteva scappare all'utente"). Stessa scelta reale ripresa poi in
            PrintOrderView, dove resta comunque modificabile. */}
        {isAnnualBook&&style&&(<>
          <div style={{fontSize:13,fontWeight:700,color:bt.tx,fontFamily:"Inter,sans-serif",marginBottom:9}}>{tr("print_photos_per_page_label")}</div>
          <div style={{fontSize:11,color:bt.soft,fontFamily:"Inter,sans-serif",marginBottom:9,lineHeight:1.4}}>{tr("print_photos_per_page_sub")}</div>
          <div style={{display:"flex",gap:10,marginBottom:20}}>
            {[1,2,4].map(n=>{
              const act=pp===n;
              return(
                <div key={n} onClick={()=>setPp(n)} style={{flex:1,background:bt.card,borderRadius:14,padding:"13px 12px",textAlign:"center",border:act?"2px solid #FF2D6B":`1px solid ${bt.bord}`,cursor:"pointer",boxShadow:act?"0 3px 12px rgba(255,45,107,.15)":"none",transition:"border .2s,box-shadow .2s"}}>
                  <div style={{fontSize:15,fontWeight:800,color:bt.tx,fontFamily:"Inter,sans-serif"}}>{n}</div>
                  <div style={{fontSize:10,color:bt.soft,fontFamily:"Inter,sans-serif",marginTop:2}}>{tr(n===1?"print_photos_per_page_singular":"print_photos_per_page_plural")}</div>
                </div>
              );
            })}
          </div>
        </>)}
        {style&&pageWarn&&(
          <div style={{fontSize:11,color:bt.soft,fontFamily:"Inter,sans-serif",lineHeight:1.5}}>
            {pageWarn.adjusted?tr("print_wizard_pages_warning",{n:contentTotal,min:pageWarn.min,max:pageWarn.max}):tr("print_wizard_pages_note",{min:pageWarn.min,max:pageWarn.max})}
          </div>
        )}
      </div>
      <div style={{padding:"12px 16px 26px",flexShrink:0}}>
        {missingReason&&<div style={{textAlign:"center",fontSize:11.5,color:bt.soft,fontFamily:"Inter,sans-serif",marginBottom:9}}>{missingReason}</div>}
        <Btn full fn={canContinue?continueNext:undefined} style={{background:canContinue?bt.pill:bt.bord,color:canContinue?"#fff":bt.soft,padding:"15px",borderRadius:40,fontSize:14,fontWeight:700,fontFamily:"Inter,sans-serif",textAlign:"center",boxShadow:canContinue?`0 7px 20px ${bt.ac}44`:"none",cursor:canContinue?"pointer":"default"}}>{tr("print_wizard_continue")}</Btn>
      </div>
    </div>
  );
}
export function PrintOrderView({t,book,go,userId,dispatch,format}){
  const bt=TH[book.theme]||t;
  // Ripiego se si arriva qui senza passare dal wizard PrintFormatView (link
  // vecchio, refresh della pagina, ecc.) — un rigido A4 verticale, la scelta
  // più comune, così la vista non si rompe mai per mancanza di formato.
  const fmt=format||{style:"hardcover",sizeId:"a4",orientation:"portrait",wIn:8.3,hIn:11.7,prodigiSku:"BOOK-FE-A4-P-HARD-G"};
  const [includeEmptyPages,setIncludeEmptyPages]=useState(false); // default OFF: libro compatto, solo pagine con contenuto
  const [copies,setCopies]=useState(1); // il PDF resta uno solo — le copie riguardano solo la quantità dell'ordine finale
  const contentTotal=(book.pages||[]).length||1;
  const isLeap=y=>(y%4===0&&y%100!==0)||y%400===0;
  const fullYearTotal=book.year===YR?DOY:(isLeap(book.year||YR)?366:365);
  // Foto per pagina — SOLO per il libro annuale (30/08/2026, richiesta di Matte): un anno
  // pieno di scatti non deve per forza stampare 365/366 pagine singole. Con 2 o 4 foto per
  // pagina il libro diventa più sottile e quindi più economico a parità di contenuto (vedi
  // il layout a griglia in generate-print-pdf/GRID_LAYOUTS, che disegna davvero N foto per
  // pagina, non è solo un conteggio finto). Per i micro-libri resta sempre 1 — non richiesto,
  // e la griglia non è mai stata pensata/testata per contenuti diversi da un anno di scatti.
  const isAnnualBook=book.type==="annual";
  const [photosPerPage,setPhotosPerPage]=useState(fmt.photosPerPage||1); // 1 | 2 | 4 — arriva già scelto dal wizard (PrintFormatView), resta comunque modificabile qui
  // La griglia ignora le pagine vuote per costruzione (non ha senso una cella vuota dentro
  // una griglia curata di foto — stessa scelta già fatta lato server): con photosPerPage>1
  // "includi pagine vuote" smette quindi di avere effetto, coerente col backend.
  const effIncludeEmptyPages=includeEmptyPages&&photosPerPage===1;
  const rawEntryCount=effIncludeEmptyPages?fullYearTotal:contentTotal;
  const total=isAnnualBook?Math.ceil(rawEntryCount/photosPerPage):rawEntryCount;
  const {pageCount:printPageCount,adjusted:pagesAdjusted,min:pageMin,max:pageMax}=clampBookPageCount(total,fmt.style);
  const pdfLayout=(isAnnualBook&&photosPerPage>1)?(photosPerPage===2?"grid2":"grid4"):"single";
  // Preventivo REALE: interroga dal vivo Gelato e/o Prodigi (Edge Function
  // get-print-quote, vedi supabase/functions) ogni volta che cambia
  // formato/pagine/copie/paese. Se le chiavi fornitore non sono ancora
  // configurate, torna una stima interna di ripiego, chiaramente etichettata.
  const [shipCountry,setShipCountry]=useState("IT");
  // Forza-fornitore (02/09/2026, admin — vedi forceVendor in get-print-quote/create-print-checkout):
  // null = automatico (vince il più economico, comportamento normale per i clienti veri).
  // "prodigi"/"gelato" = usa SEMPRE quello, anche a costo più alto — serve per poter ordinare
  // davvero lo stesso libro su entrambi e confrontare fisicamente il risultato (dorso, copertina,
  // ecc.) prima di considerare completo il lavoro sul dorso "il tuo {anno}".
  const [forceVendor,setForceVendor]=useState(null);
  const [quote,setQuote]=useState({loading:false,data:null,error:null});
  useEffect(()=>{
    let cancelled=false;
    setQuote(q=>({...q,loading:true,error:null}));
    fetchPrintQuote({format:{...fmt,pageCount:printPageCount},copies,shippingCountry:shipCountry,forceVendor}).then(res=>{
      if(cancelled) return;
      if(res&&res.ok) setQuote({loading:false,data:res,error:null});
      else setQuote({loading:false,data:null,error:(res&&res.error)||tr("print_quote_error")});
    });
    return ()=>{cancelled=true;};
  },[fmt.style,fmt.sizeId,fmt.orientation,printPageCount,copies,shipCountry,forceVendor]);
  const fallback=calcPrintPrice(printPageCount,fmt.style==="softcover"?"pocket":"standard"); // stima di ripiego, mai mostrata se il preventivo live funziona
  const unitPrice=quote.data?quote.data.price:fallback.price;
  const totalPrice=unitPrice*copies;
  const shipDays=quote.data?quote.data.shipDays:fallback.shipDays;
  const vendorLabel=quote.data&&quote.data.vendorLabel;
  // Libro e spedizione separati (aggiunto il 31/08/2026, su richiesta di Matte): un preventivo
  // reale ha sempre bookPrice+shippingPrice; la stima di ripiego (fallback) no, perché è un unico
  // numero stimato prima ancora di sapere quale fornitore/spedizione useremo — in quel caso non
  // mostriamo la riga di dettaglio, solo il totale come prima.
  const bookPriceTotal=quote.data?quote.data.bookPrice*copies:null;
  const shippingPriceTotal=quote.data?quote.data.shippingPrice*copies:null;
  const usingFallback=!quote.data&&!quote.loading;
  // PDF di stampa: generato una volta sola per combinazione formato/pagine,
  // poi riusato sia per il test manuale sia per l'ordine reale — create-print-checkout
  // lo richiede come asset da mandare al fornitore.
  const [pdfBusy,setPdfBusy]=useState(false);
  const [pdfUrl,setPdfUrl]=useState(null);
  // La Edge Function generate-print-pdf conosce le dimensioni fisiche per
  // chiave "{sizeId}_{orientation}" (vedi TRIM_MM lì dentro) — non lo stile
  // (rigida/morbida/layflat), che riguarda solo la copertina fisica scelta
  // dal fornitore, non la geometria delle pagine interne del PDF.
  const pdfFormatKey=`${fmt.sizeId}_${fmt.orientation}`;
  // Il PDF già generato (pdfUrl) va invalidato se cambia qualunque cosa che ne modifichi il
  // contenuto — prima valeva solo per il caso "un solo PDF per vista", ora che photosPerPage
  // può cambiare DOPO aver già generato un PDF di test, senza questo reset l'utente vedrebbe
  // ancora il vecchio PDF a 1 foto/pagina anche dopo aver scelto 4 foto/pagina.
  useEffect(()=>{ setPdfUrl(null); },[pdfLayout,printPageCount,fmt.sizeId,fmt.orientation]);
  const ensurePdf=async()=>{
    if(pdfUrl) return pdfUrl;
    setPdfBusy(true);
    const res=await generatePrintPdf({
      userId,
      bookTitle:book.title,
      // dateMs: data VERA della voce (millisecondi) — libro annuale la porta già come p.ts
      // (creato in App.jsx da pages.created_at), libro condiviso/micro come p.createdAt
      // (stringa ISO, vedi Micro.jsx). Prima di questo fix (01/09/2026, segnalato da Matte sul
      // libro di prova stampato) qui veniva mandato solo "day" — per il libro annuale il server
      // ricostruiva la data da day+anno CORRENTE (sbagliata per un libro di un anno passato),
      // per i libri condivisi "day" è solo un numero di pagina progressivo (pg.n in Micro.jsx),
      // quindi la data stampata sarebbe stata del tutto inventata. Vedi generate-print-pdf/
      // index.ts, pageDateText().
      pages:(book.pages||[]).map(p=>({day:p.day,photo:p.photo,caption:p.caption||null,dateMs:p.ts!=null?p.ts:(p.createdAt?new Date(p.createdAt).getTime():null)})),
      totalDaysInYear:fullYearTotal,
      format:pdfFormatKey,
      includeEmptyPages:effIncludeEmptyPages,
      themeColor:bt.ac,
      layout:pdfLayout,
      // Stesso numero già usato per preventivo/ordine (clampBookPageCount(total,fmt.style),
      // vedi printPageCount sopra) — così il PDF caricato dal fornitore ha sempre lo stesso
      // conteggio pagine dichiarato, anche per un micro-libro/condiviso troppo corto da solo
      // per raggiungere il minimo (24 pagine rigida/morbida A4). Vedi minPageCount in
      // generate-print-pdf/index.ts.
      minPageCount:printPageCount,
    });
    setPdfBusy(false);
    if(res.ok&&res.pdfUrl){ setPdfUrl(res.pdfUrl); return res.pdfUrl; }
    pushToast(dispatch,res.error||tr("print_pdf_test_error"));
    return null;
  };
  // ── Debug ammin: tabella prezzi per numero di pagine ────────────────────
  // TEMPORANEO — serve solo a Matte per farsi un'idea reale del margine (costo
  // fornitore + guadagno Oncer, vedi applyMargin in get-print-quote) su formati
  // diversi, prima di decidere se il guadagno impostato va bene. Richiama la
  // STESSA Edge Function reale (get-print-quote) una volta per ogni numero di
  // pagine qui sotto — numeri veri di Prodigi/Gelato, non stime. Da rimuovere
  // (o nascondere meglio) quando il margine sarà confermato definitivo.
  // Aggiornato il 02/09/2026 (richiesta di Matte): i 4 tagli usati per i
  // pacchetti foto di Oncer Event (50/100/150/200) — invariati rispetto a
  // prima nel FUNZIONAMENTO, solo nella lista di pagine interrogate, per
  // leggere qui direttamente i 4 prezzi da riportare nel documento fotografi,
  // senza dover creare nessun libro finto per "testare" la stampa.
  const PRICE_TABLE_PAGE_COUNTS=[50,100,150,200];
  const [priceTable,setPriceTable]=useState(null); // null | "loading" | array di risultati
  const runPriceTable=async()=>{
    setPriceTable("loading");
    const rows=[];
    for(const n of PRICE_TABLE_PAGE_COUNTS){
      const clamped=clampBookPageCount(n,fmt.style).pageCount;
      const res=await fetchPrintQuote({format:{...fmt,pageCount:clamped},copies:1,shippingCountry:shipCountry});
      rows.push({n:clamped,requested:n,ok:!!(res&&res.ok),vendorPrice:res&&res.vendorPrice,price:res&&res.price,vendorLabel:res&&res.vendorLabel,error:res&&res.error});
    }
    setPriceTable(rows);
  };
  // Larghezza dorso Prodigi per numero di pagine (03/09/2026, richiesta di Matte prima di ordinare
  // una stampa di prova fisica: "quante pagine servono per un dorso leggibile"). Dato PURAMENTE
  // informativo (POST /v4.0/products/spine di Prodigi) — nessun ordine, nessuna spesa, interrogabile
  // quante volte si vuole. Serve a scegliere il numero di pagine giusto PRIMA di spendere soldi in
  // una stampa vera, invece di indovinare (vedi chat: 22 pagine erano chiaramente troppo poche, ma
  // "quante ne servono davvero" va chiesto a Prodigi, non stimato).
  const SPINE_TABLE_PAGE_COUNTS=[24,40,60,80,100,150,200,250,300];
  const [spineTable,setSpineTable]=useState(null); // null | "loading" | {sku,rows}
  const runSpineTable=async()=>{
    setSpineTable("loading");
    const res=await fetchPrintQuote({format:fmt,copies:1,shippingCountry:shipCountry,debugSpinePageCounts:SPINE_TABLE_PAGE_COUNTS});
    if(res&&res.ok) setSpineTable({sku:res.sku,rows:res.spineTable});
    else setSpineTable({error:(res&&res.error)||"errore sconosciuto"});
  };
  // Indirizzo di spedizione + avvio pagamento reale (Stripe Checkout, vedi
  // create-print-checkout). L'ordine arriva al fornitore solo DOPO il
  // pagamento confermato — via stripe-print-webhook, non da qui — così non
  // rischiamo mai di spedire un ordine a pagamento fallito o abbandonato.
  const [showShipping,setShowShipping]=useState(false);
  const [ship,setShip]=useState({name:"",address1:"",address2:"",city:"",postcode:"",state:"",country:"IT",phone:"",email:""});
  const shipField=(k)=>(e)=>setShip(s=>({...s,[k]:e.target.value}));
  const shippingComplete=!!(ship.name&&ship.address1&&ship.city&&ship.postcode&&ship.country&&ship.email);
  const [checkoutBusy,setCheckoutBusy]=useState(false);
  const goToShipping=()=>{
    if(ship.country&&ship.country.toUpperCase()!==shipCountry) setShipCountry(ship.country.toUpperCase());
    setShowShipping(true);
  };
  const startCheckout=async()=>{
    if(!shippingComplete){ pushToast(dispatch,tr("print_shipping_incomplete")); return; }
    setCheckoutBusy(true);
    const finalPdfUrl=await ensurePdf();
    if(!finalPdfUrl){ setCheckoutBusy(false); return; }
    const res=await createPrintCheckout({
      userId,
      book:{id:book.id,type:book.type,title:book.title,year:book.year||null,theme:book.theme},
      format:{...fmt,pageCount:printPageCount},
      copies,
      pdfUrl:finalPdfUrl,
      shipping:{...ship,country:ship.country.toUpperCase()},
      quote:quote.data||{price:unitPrice,shipDays,fallback:true},
      forceVendor,
    });
    setCheckoutBusy(false);
    if(res.ok&&res.checkoutUrl){ window.open(res.checkoutUrl,"_blank"); pushToast(dispatch,tr("print_order_pending_body")); }
    else if(res.notConfigured){ pushToast(dispatch,tr("print_checkout_not_configured")); }
    else { pushToast(dispatch,res.error||tr("print_checkout_error")); }
  };
  return(
    <div className="screen fade" style={{background:bt.bg}}>
      <div style={{padding:"14px 16px",display:"flex",alignItems:"center",gap:11,flexShrink:0,borderBottom:`1px solid ${bt.bord}`,background:bt.card}}>
        <div onClick={()=>(showShipping?setShowShipping(false):go("print_format",{book}))} style={{width:32,height:32,borderRadius:"50%",background:bt.bord,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:14,color:bt.tx}}>←</div>
        <span style={{fontSize:16,fontWeight:800,color:bt.tx,fontFamily:"Inter,sans-serif"}}>{showShipping?tr("print_shipping_header"):tr("print_order_header")}</span>
      </div>
      {showShipping?(
        <div style={{flex:1,overflowY:"auto",padding:"18px 16px 20px"}}>
          {[
            {k:"name",label:tr("print_shipping_name")},
            {k:"address1",label:tr("print_shipping_address1")},
            {k:"address2",label:tr("print_shipping_address2")},
            {k:"city",label:tr("print_shipping_city")},
            {k:"postcode",label:tr("print_shipping_postcode")},
            {k:"state",label:tr("print_shipping_state")},
            {k:"country",label:tr("print_shipping_country")},
            {k:"phone",label:tr("print_shipping_phone")},
            {k:"email",label:tr("print_shipping_email")},
          ].map(f=>(
            <div key={f.k} style={{marginBottom:13}}>
              <div style={{fontSize:11,fontWeight:700,color:bt.soft,fontFamily:"Inter,sans-serif",marginBottom:5}}>{f.label}</div>
              <input value={ship[f.k]} onChange={shipField(f.k)} style={{width:"100%",boxSizing:"border-box",background:bt.card,border:`1px solid ${bt.bord}`,borderRadius:11,padding:"11px 13px",fontSize:13,color:bt.tx,fontFamily:"Inter,sans-serif",outline:"none"}}/>
            </div>
          ))}
        </div>
      ):(
      <div style={{flex:1,overflowY:"auto",padding:"18px 16px 20px"}}>
        {/* 3D-ish book mockup */}
        <div style={{display:"flex",justifyContent:"center",marginBottom:22,position:"relative",height:170}}>
          {[6,3,0].map((offset,i)=>(
            <div key={i} style={{
              position:"absolute",top:offset*0.6,left:"50%",
              transform:`translateX(calc(-50% + ${offset}px)) rotate(${offset*0.6}deg)`,
              width:126,height:160,borderRadius:"3px 10px 10px 3px",
              background:i===2?bt.pill:bt.ac+"33",
              boxShadow:"0 10px 24px rgba(0,0,0,.22)",
              display:i===2?"flex":"none",flexDirection:"column",alignItems:"center",justifyContent:"center",
              border:"1px solid rgba(255,255,255,.25)",
            }}>
              <div style={{fontSize:30,marginBottom:8}}>{bt.emoji}</div>
              <div style={{fontSize:11,fontWeight:800,color:"#fff",fontFamily:"Inter,sans-serif",textAlign:"center",padding:"0 10px"}}>{book.title}</div>
              <div style={{fontSize:9,color:"rgba(255,255,255,.7)",fontFamily:"Inter,sans-serif",marginTop:4}}>{printPageCount} {tr("feed_page_plural")}</div>
            </div>
          ))}
          {[0,1].map(i=>(
            <div key={i} style={{position:"absolute",top:(2-i)*3,left:`calc(50% + ${(2-i)*6}px)`,transform:"translateX(-50%)",width:126,height:160,borderRadius:"3px 10px 10px 3px",background:bt.ac+"22",border:"1px solid rgba(0,0,0,.05)",zIndex:0}}/>
          ))}
        </div>
        {/* Riepilogo formato scelto nel wizard — non più selezionabile qui,
            solo modificabile tornando indietro, per non duplicare la logica
            di ramificazione già gestita da PrintFormatView. */}
        <div onClick={()=>go("print_format",{book})} style={{display:"flex",alignItems:"center",gap:12,background:bt.card,borderRadius:14,padding:"13px 15px",border:`1px solid ${bt.bord}`,marginBottom:18,cursor:"pointer"}}>
          <div style={{flex:1}}>
            <div style={{fontSize:10,fontWeight:700,color:bt.soft,fontFamily:"Inter,sans-serif",letterSpacing:"1px",textTransform:"uppercase",marginBottom:3}}>{tr("print_format_label")}</div>
            <div style={{fontSize:13,fontWeight:700,color:bt.tx,fontFamily:"Inter,sans-serif"}}>
              {fmt.style==="layflat"?tr("print_wizard_layflat_note").split("—")[0].trim():fmt.style==="softcover"?tr("print_wizard_cover_soft"):tr("print_wizard_cover_hard")} · {fmt.wIn?.toFixed?.(1)}×{fmt.hIn?.toFixed?.(1)}"
            </div>
          </div>
          <span style={{fontSize:11,color:"#FF2D6B",fontWeight:700,fontFamily:"Inter,sans-serif"}}>{tr("print_wizard_continue")} ›</span>
        </div>
        {/* Foto per pagina — SOLO libro annuale (30/08/2026). Vedi nota su isAnnualBook/
            photosPerPage sopra: con più foto per pagina il libro ha meno pagine e costa meno,
            restando comunque "l'anno intero" — solo più compatto. */}
        {isAnnualBook&&(
          <div style={{background:bt.card,borderRadius:14,padding:"14px 15px",border:`1px solid ${bt.bord}`,marginBottom:18}}>
            <div style={{fontSize:13,fontWeight:700,color:bt.tx,fontFamily:"Inter,sans-serif",marginBottom:3}}>{tr("print_photos_per_page_label")}</div>
            <div style={{fontSize:11,color:bt.soft,fontFamily:"Inter,sans-serif",marginBottom:12,lineHeight:1.4}}>{tr("print_photos_per_page_sub")}</div>
            <div style={{display:"flex",gap:8}}>
              {[1,2,4].map(n=>{
                const act=photosPerPage===n;
                return(
                  <div key={n} onClick={()=>setPhotosPerPage(n)} style={{flex:1,textAlign:"center",padding:"10px 6px",borderRadius:12,border:act?"2px solid #FF2D6B":`1px solid ${bt.bord}`,background:act?"#FF2D6B14":"transparent",cursor:"pointer"}}>
                    <div style={{fontSize:14,fontWeight:800,color:bt.tx,fontFamily:"Inter,sans-serif"}}>{n}</div>
                    <div style={{fontSize:9.5,color:bt.soft,fontFamily:"Inter,sans-serif",marginTop:1}}>{tr(n===1?"print_photos_per_page_singular":"print_photos_per_page_plural")}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* Toggle pagine vuote — nascosto quando la griglia (photosPerPage>1) lo rende senza
            effetto, invece di lasciarlo lì acceso/spento senza che cambi nulla (stesso principio
            usato per il bottone "Continua": mai un controllo il cui effetto non si capisce). */}
        {photosPerPage===1&&(
        <div style={{display:"flex",alignItems:"center",gap:12,background:bt.card,borderRadius:14,padding:"14px 15px",border:`1px solid ${bt.bord}`,marginBottom:18}}>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:700,color:bt.tx,fontFamily:"Inter,sans-serif"}}>{tr("print_empty_pages_label")}</div>
            <div style={{fontSize:11,color:bt.soft,fontFamily:"Inter,sans-serif",marginTop:3,lineHeight:1.4}}>{tr("print_empty_pages_sub")}</div>
          </div>
          <Toggle on={includeEmptyPages} onClick={()=>setIncludeEmptyPages(!includeEmptyPages)}/>
        </div>
        )}
        {pagesAdjusted&&(
          <div style={{fontSize:11,color:bt.soft,fontFamily:"Inter,sans-serif",marginTop:-11,marginBottom:18,lineHeight:1.5}}>{tr("print_wizard_pages_warning",{n:total,min:pageMin,max:pageMax})}</div>
        )}
        {/* Numero di copie: utile soprattutto per i libri condivisi, dove più
            persone potrebbero volere la propria copia dello stesso libro. */}
        <div style={{display:"flex",alignItems:"center",gap:12,background:bt.card,borderRadius:14,padding:"14px 15px",border:`1px solid ${bt.bord}`,marginBottom:18}}>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:700,color:bt.tx,fontFamily:"Inter,sans-serif"}}>{tr("print_copies_label")}</div>
            <div style={{fontSize:11,color:bt.soft,fontFamily:"Inter,sans-serif",marginTop:3,lineHeight:1.4}}>
              {book.shared&&(book.membersCount||0)>1?tr("print_copies_shared_hint",{n:book.membersCount}):tr("print_copies_sub")}
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
            <div onClick={()=>setCopies(c=>Math.max(1,c-1))} style={{width:30,height:30,borderRadius:"50%",background:bt.bord,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:16,fontWeight:700,color:bt.tx,userSelect:"none"}}>−</div>
            <span style={{fontSize:15,fontWeight:800,color:bt.tx,fontFamily:"Inter,sans-serif",minWidth:18,textAlign:"center"}}>{copies}</span>
            <div onClick={()=>setCopies(c=>Math.min(20,c+1))} style={{width:30,height:30,borderRadius:"50%",background:"#FF2D6B",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:16,fontWeight:700,color:"#fff",userSelect:"none"}}>+</div>
          </div>
        </div>
        {/* Convincing feature list */}
        <div style={{background:bt.card,borderRadius:14,padding:"16px 15px",border:`1px solid ${bt.bord}`,marginBottom:18}}>
          {[
            tr("print_feature_cover"),
            tr("print_feature_pages"),
            tr("print_feature_delivery",{min:shipDays,max:shipDays+2}),
            tr("print_feature_memory"),
          ].map((f,i)=>(
            <div key={i} style={{display:"flex",alignItems:"flex-start",gap:9,marginBottom:i<3?10:0}}>
              <span style={{color:"#FF2D6B",fontSize:13,marginTop:1}}>✓</span>
              <span style={{fontSize:12.5,color:bt.tx,fontFamily:"Inter,sans-serif",lineHeight:1.45}}>{f}</span>
            </div>
          ))}
        </div>
        {/* Price box — preventivo REALE dal vivo, non più la stima finta */}
        <div style={{background:"linear-gradient(135deg,#1A0A2E,#2D0A3E)",borderRadius:16,padding:"18px 18px",marginBottom:6}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
            <span style={{fontSize:12,color:"rgba(255,255,255,.6)",fontFamily:"Inter,sans-serif"}}>{tr("print_price_label",{year:book.year||tr("print_price_fallback")})}</span>
            <span style={{fontSize:26,fontWeight:900,color:"#fff",fontFamily:"Inter,sans-serif"}}>{quote.loading?"…":`€${totalPrice.toFixed(2)}`}</span>
          </div>
          <div style={{fontSize:10,color:"rgba(255,255,255,.4)",fontFamily:"Inter,sans-serif"}}>{quote.loading?tr("print_quote_loading"):tr("print_price_note")}</div>
          {!quote.loading&&bookPriceTotal!=null&&shippingPriceTotal!=null&&(
            <div style={{fontSize:10.5,color:"rgba(255,255,255,.55)",fontFamily:"Inter,sans-serif",marginTop:5}}>{tr("print_price_breakdown",{book:bookPriceTotal.toFixed(2),shipping:shippingPriceTotal.toFixed(2)})}</div>
          )}
          {copies>1&&<div style={{fontSize:10,color:"rgba(255,255,255,.4)",fontFamily:"Inter,sans-serif",marginTop:3}}>{tr("print_copies_multiplier_note",{unit:unitPrice.toFixed(2),n:copies})}</div>}
          {vendorLabel&&<div style={{fontSize:10,color:"rgba(255,255,255,.4)",fontFamily:"Inter,sans-serif",marginTop:3}}>{tr("print_quote_vendor_note",{vendor:vendorLabel,min:shipDays,max:shipDays+2})}</div>}
          {usingFallback&&<div style={{fontSize:10,color:"#FFB84D",fontFamily:"Inter,sans-serif",marginTop:6,lineHeight:1.4}}>{tr("print_quote_fallback_note")}</div>}
        </div>
        {/* Test admin: genera il PDF di stampa vero (Edge Function generate-print-pdf)
            per controllarlo a mano prima di mandarlo al fornitore. Visibile solo qui
            perché questa vista intera è già raggiungibile solo da account admin. */}
        <div style={{background:bt.card,borderRadius:14,padding:"14px 15px",border:`1px dashed ${bt.bord}`,marginBottom:6}}>
          <div style={{fontSize:11,fontWeight:700,color:bt.tx,fontFamily:"Inter,sans-serif",marginBottom:3}}>{tr("print_pdf_test_label")}</div>
          <div style={{fontSize:10.5,color:bt.soft,fontFamily:"Inter,sans-serif",marginBottom:10,lineHeight:1.4}}>{tr("print_pdf_test_sub")}</div>
          <Btn fn={pdfBusy?undefined:ensurePdf} style={{background:bt.bord,color:bt.tx,padding:"10px 14px",borderRadius:12,fontSize:12,fontWeight:700,fontFamily:"Inter,sans-serif",textAlign:"center",opacity:pdfBusy?.6:1}}>{pdfBusy?tr("print_pdf_test_busy"):tr("print_pdf_test_cta")}</Btn>
          {pdfUrl&&(
            <a href={pdfUrl} target="_blank" rel="noreferrer" style={{display:"block",marginTop:9,fontSize:11.5,color:"#FF2D6B",fontFamily:"Inter,sans-serif",fontWeight:700,textDecoration:"underline"}}>{tr("print_pdf_test_ready")}</a>
          )}
        </div>
        {/* Debug ammin TEMPORANEO — tabella costo/guadagno reale per numero di pagine, vedi nota sopra runPriceTable */}
        <div style={{background:bt.card,borderRadius:14,padding:"14px 15px",border:"1px dashed #FFB84D",marginBottom:6}}>
          <div style={{fontSize:11,fontWeight:700,color:bt.tx,fontFamily:"Inter,sans-serif",marginBottom:3}}>Debug: prezzo reale per numero di pagine</div>
          <div style={{fontSize:10.5,color:bt.soft,fontFamily:"Inter,sans-serif",marginBottom:10,lineHeight:1.4}}>Interroga {PRICE_TABLE_PAGE_COUNTS.length} volte il preventivo reale ({PRICE_TABLE_PAGE_COUNTS.join(", ")} pagine, stesso formato scelto sopra) per vedere costo fornitore e prezzo finale con il margine attuale.</div>
          <Btn fn={priceTable==="loading"?undefined:runPriceTable} style={{background:bt.bord,color:bt.tx,padding:"10px 14px",borderRadius:12,fontSize:12,fontWeight:700,fontFamily:"Inter,sans-serif",textAlign:"center",opacity:priceTable==="loading"?.6:1}}>{priceTable==="loading"?"Sto calcolando…":"Calcola tabella prezzi"}</Btn>
          {Array.isArray(priceTable)&&(
            <div style={{marginTop:12,fontFamily:"Space Mono,monospace",fontSize:11}}>
              <div style={{display:"flex",fontWeight:700,color:bt.soft,marginBottom:6}}>
                <div style={{width:60}}>Pagine</div><div style={{width:80}}>Fornitore</div><div style={{width:90}}>Costo</div><div style={{width:90}}>Guadagno</div><div style={{flex:1}}>Prezzo finale</div>
              </div>
              {priceTable.map((r,i)=>(
                <div key={i} style={{display:"flex",color:bt.tx,marginBottom:4}}>
                  <div style={{width:60}}>{r.n}{r.requested!==r.n?` (${r.requested})`:""}</div>
                  <div style={{width:80}}>{r.ok?(r.vendorLabel||"—"):"—"}</div>
                  <div style={{width:90}}>{r.ok?`€${Number(r.vendorPrice).toFixed(2)}`:"—"}</div>
                  <div style={{width:90}}>{r.ok?`€${(Number(r.price)-Number(r.vendorPrice)).toFixed(2)}`:"—"}</div>
                  <div style={{flex:1,fontWeight:700}}>{r.ok?`€${Number(r.price).toFixed(2)}`:(r.error||"errore")}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Debug ammin TEMPORANEO — larghezza dorso Prodigi per numero di pagine, vedi nota sopra
            runSpineTable. Dato gratuito, nessun ordine coinvolto. */}
        <div style={{background:bt.card,borderRadius:14,padding:"14px 15px",border:"1px dashed #FFB84D",marginBottom:6}}>
          <div style={{fontSize:11,fontWeight:700,color:bt.tx,fontFamily:"Inter,sans-serif",marginBottom:3}}>Debug: larghezza dorso Prodigi per numero di pagine</div>
          <div style={{fontSize:10.5,color:bt.soft,fontFamily:"Inter,sans-serif",marginBottom:10,lineHeight:1.4}}>Interroga Prodigi (gratis, nessun ordine) per lo SKU sopra scelto e dice quanti mm di dorso avrebbe il libro a {SPINE_TABLE_PAGE_COUNTS.join(", ")} pagine — utile per capire quante pagine servono davvero prima di stampare una prova fisica.</div>
          <Btn fn={spineTable==="loading"?undefined:runSpineTable} style={{background:bt.bord,color:bt.tx,padding:"10px 14px",borderRadius:12,fontSize:12,fontWeight:700,fontFamily:"Inter,sans-serif",textAlign:"center",opacity:spineTable==="loading"?.6:1}}>{spineTable==="loading"?"Sto interrogando Prodigi…":"Calcola larghezza dorso"}</Btn>
          {spineTable&&spineTable.error&&(
            <div style={{marginTop:10,fontSize:11,color:"#c0392b",fontFamily:"Inter,sans-serif"}}>{spineTable.error}</div>
          )}
          {spineTable&&Array.isArray(spineTable.rows)&&(
            <div style={{marginTop:12,fontFamily:"Space Mono,monospace",fontSize:11}}>
              <div style={{display:"flex",fontWeight:700,color:bt.soft,marginBottom:6}}>
                <div style={{width:60}}>Pagine</div><div style={{flex:1}}>Larghezza dorso</div>
              </div>
              {spineTable.rows.map((r,i)=>(
                <div key={i} style={{display:"flex",color:bt.tx,marginBottom:4}}>
                  <div style={{width:60}}>{r.pageCount}</div>
                  <div style={{flex:1,fontWeight:700}}>{r.ok?`${r.widthMm} mm`:(r.error||"errore")}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Debug ammin TEMPORANEO — forza-fornitore per ordini di test reali (02/09/2026, vedi
            nota su forceVendor sopra). Serve a ordinare davvero lo stesso libro su Prodigi e su
            Gelato per confrontare il risultato fisico (dorso, copertina) prima di dare per buona
            l'implementazione del dorso "il tuo {anno}", che sui due fornitori funziona in modo
            tecnicamente diverso. "Automatico" = comportamento normale (vince il più economico). */}
        <div style={{background:bt.card,borderRadius:14,padding:"14px 15px",border:"1px dashed #FFB84D",marginBottom:6}}>
          <div style={{fontSize:11,fontWeight:700,color:bt.tx,fontFamily:"Inter,sans-serif",marginBottom:3}}>Debug: forza fornitore per ordine di test</div>
          <div style={{fontSize:10.5,color:bt.soft,fontFamily:"Inter,sans-serif",marginBottom:10,lineHeight:1.4}}>Normalmente vince il più economico tra Prodigi e Gelato. Per un ordine di test, forza qui quale dei due usare — vale sia per il preventivo qui sopra sia per l'ordine vero che parte al pagamento.</div>
          <div style={{display:"flex",gap:8}}>
            {[{k:null,label:"Automatico"},{k:"prodigi",label:"Forza Prodigi"},{k:"gelato",label:"Forza Gelato"}].map(opt=>(
              <div key={String(opt.k)} onClick={()=>setForceVendor(opt.k)} style={{flex:1,textAlign:"center",padding:"9px 8px",borderRadius:11,fontSize:11.5,fontWeight:700,fontFamily:"Inter,sans-serif",cursor:"pointer",background:forceVendor===opt.k?"#FFB84D":bt.bord,color:forceVendor===opt.k?"#1a1200":bt.tx}}>{opt.label}</div>
            ))}
          </div>
          {forceVendor&&(
            <div style={{marginTop:9,fontSize:10.5,color:"#B45309",fontFamily:"Inter,sans-serif",lineHeight:1.4}}>⚠️ Fornitore forzato su <b>{forceVendor==="gelato"?"Gelato":"Prodigi"}</b> — il preventivo sopra e l'ordine reale al pagamento useranno sempre lui, anche se costasse di più. Ricordati di rimettere "Automatico" dopo il test.</div>
          )}
        </div>
      </div>
      )}
      <div style={{padding:"12px 16px 26px",flexShrink:0,background:bt.card,borderTop:`1px solid ${bt.bord}`}}>
        {showShipping?(<>
          {/* Prima il bottone SEMBRAVA spento quando mancavano campi spedizione ma in
              realtà era sempre cliccabile — il toast d'errore compariva solo DOPO il
              click, senza nessun avviso prima (bug segnalato da Matte il 30/08/2026).
              Ora il bottone è DAVVERO disabilitato in quel caso, con lo stesso avviso
              mostrato PRIMA, non dopo. */}
          {!shippingComplete&&!checkoutBusy&&(
            <div style={{textAlign:"center",fontSize:11.5,color:bt.soft,fontFamily:"Inter,sans-serif",marginBottom:9}}>{tr("print_shipping_incomplete")}</div>
          )}
          <Btn full fn={(checkoutBusy||!shippingComplete)?undefined:startCheckout} style={{background:shippingComplete?"linear-gradient(135deg,#FF2D6B,#7B2FFF)":bt.bord,color:shippingComplete?"#fff":bt.soft,padding:"16px",borderRadius:40,fontSize:15,fontWeight:700,fontFamily:"Inter,sans-serif",textAlign:"center",boxShadow:shippingComplete?"0 8px 22px rgba(255,45,107,.35)":"none",opacity:checkoutBusy?.6:1}}>
            {checkoutBusy?tr("print_checkout_busy"):(quote.loading?tr("print_quote_loading"):tr("print_checkout_cta",{price:totalPrice.toFixed(2)}))}
          </Btn>
        </>):(
          // Finché il preventivo reale sta ancora caricando, NON mostriamo qui un prezzo
          // (prima si vedeva il prezzo di stima di ripiego per una frazione di secondo/minuto,
          // dando l'impressione sbagliata che fosse già il prezzo reale) — solo dopo che
          // quote.loading diventa false (prezzo reale O stima di ripiego dichiarata) mostriamo il CTA con prezzo.
          <Btn full fn={quote.loading?undefined:goToShipping} style={{background:"linear-gradient(135deg,#FF2D6B,#7B2FFF)",color:"#fff",padding:"16px",borderRadius:40,fontSize:15,fontWeight:700,fontFamily:"Inter,sans-serif",textAlign:"center",boxShadow:"0 8px 22px rgba(255,45,107,.35)",opacity:quote.loading?.6:1}}>{quote.loading?tr("print_quote_loading"):tr("print_order_cta",{price:totalPrice.toFixed(2)})}</Btn>
        )}
      </div>
    </div>
  );
}
// Punto d'ingresso unico per la stampa, raggiunto da Impostazioni → Stampa.
// Prima c'era solo la scorciatoia diretta al libro dell'anno (via Library.jsx);
// i micro-libri e i libri condivisi non avevano nessun punto d'accesso visibile
// da qui — esistevano solo scorciatoie sepolte dentro le rispettive schermate.
// Questa vista elenca tutto ciò che l'utente può stampare oggi e lo manda
// al posto giusto: libro dell'anno → direttamente all'anteprima di stampa
// (i dati delle pagine sono già in st.books); micro-libri e libri condivisi →
// dentro la loro scheda di dettaglio, dove le pagine vengono caricate per
// intero e dove ora il pulsante di stampa è visibile anche a libro ancora
// aperto, non solo quando è chiuso.
export function PrintHubView({t,st,go}){
  const annualBook=st.books&&st.books[YR]||{pages:[]};
  const annualFilled=annualBook.pages?.length||0;
  const myMicro=(st.micro||[]).filter(b=>(b.pages||0)>0);
  const sharedBooks=(st.sharedBooks||[]).filter(b=>(b.pages||0)>0);
  const nothingToPrint=annualFilled===0&&myMicro.length===0&&sharedBooks.length===0;
  const Card=({icon,title,sub,status,onClick})=>(
    <div onClick={onClick} style={{background:t.card,borderRadius:13,border:`1px solid ${t.bord}`,overflow:"hidden",display:"flex",alignItems:"center",marginBottom:9,cursor:"pointer"}}>
      <div style={{width:50,height:50,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,background:t.pill}}>{icon}</div>
      <div style={{padding:"10px 12px",flex:1,minWidth:0}}>
        <div style={{fontSize:13,fontWeight:700,color:t.tx,fontFamily:"Inter,sans-serif",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{title}</div>
        <div style={{fontSize:10,color:t.soft,fontFamily:"Inter,sans-serif",marginTop:3}}>{sub}</div>
      </div>
      {status&&<span style={{fontSize:9,padding:"3px 9px",borderRadius:20,background:t.ac+"18",color:t.ac,fontFamily:"Inter,sans-serif",fontWeight:600,marginRight:12,flexShrink:0}}>{status}</span>}
      <span style={{fontSize:14,color:t.soft,marginRight:13,flexShrink:0}}>›</span>
    </div>
  );
  return(
    <div className="screen fade" style={{background:t.bg,display:"flex",flexDirection:"column"}}>
      <div style={{padding:"14px 16px",display:"flex",alignItems:"center",gap:11,flexShrink:0,borderBottom:`1px solid ${t.bord}`}}>
        <Back fn={()=>go("settings")} t={t}/>
        <span style={{fontSize:16,fontWeight:800,color:t.tx,fontFamily:"Inter,sans-serif"}}>{tr("print_hub_header")}</span>
      </div>
      <div style={{flex:1,overflowY:"auto",padding:"18px 16px 20px"}}>
        {nothingToPrint?(
          <div style={{textAlign:"center",padding:"40px 20px"}}>
            <div style={{fontSize:40,marginBottom:12}}>🖨</div>
            <div style={{fontSize:15,fontWeight:700,color:t.tx,fontFamily:"Inter,sans-serif",marginBottom:7}}>{tr("print_hub_empty_title")}</div>
            <div style={{fontSize:12,color:t.soft,fontFamily:"Inter,sans-serif"}}>{tr("print_hub_empty_sub")}</div>
          </div>
        ):(<>
          {annualFilled>0&&<>
            <div style={{fontSize:10,fontWeight:700,color:t.soft,fontFamily:"Inter,sans-serif",letterSpacing:"1.5px",textTransform:"uppercase",marginBottom:10}}>{tr("print_hub_annual_section")}</div>
            <Card
              icon="📖"
              title={tr("lib_header_title_mine",{year:YR})}
              sub={`${annualFilled} ${tr(annualFilled===1?"feed_page_singular":"feed_page_plural")}`}
              onClick={()=>go("print_preview",{book:{type:"annual",year:YR,title:`Il tuo ${YR}`,theme:st.myTheme||t.id,pages:annualBook.pages}})}
            />
          </>}
          {myMicro.length>0&&<>
            <div style={{fontSize:10,fontWeight:700,color:t.soft,fontFamily:"Inter,sans-serif",letterSpacing:"1.5px",textTransform:"uppercase",marginBottom:10,marginTop:annualFilled>0?18:0}}>{tr("print_hub_micro_section")}</div>
            {myMicro.map(b=>(
              <Card
                key={b.id}
                icon={b.cover||"📎"}
                title={b.title}
                sub={`${b.pages} ${tr(b.pages===1?"micro_page_singular":"micro_page_plural")}`}
                status={b.closed?tr("print_hub_status_done"):tr("micro_in_progress")}
                onClick={()=>go("micro_open_"+b.id)}
              />
            ))}
          </>}
          {sharedBooks.length>0&&<>
            <div style={{fontSize:10,fontWeight:700,color:t.soft,fontFamily:"Inter,sans-serif",letterSpacing:"1.5px",textTransform:"uppercase",marginBottom:10,marginTop:(annualFilled>0||myMicro.length>0)?18:0}}>{tr("micro_shared_books")}</div>
            {sharedBooks.map(b=>(
              <Card
                key={b.id}
                icon={b.cover||"🤝"}
                title={b.title}
                sub={`${b.pages} ${tr(b.pages===1?"micro_page_singular":"micro_page_plural")}`}
                status={b.closed?tr("print_hub_status_done"):tr("micro_in_progress")}
                onClick={()=>go("micro_open_shared_"+b.id)}
              />
            ))}
          </>}
        </>)}
      </div>
    </div>
  );
}
